import { useEffect } from 'react'
import { enableTabbing, disableTabbing } from '@utils/focusable'

export function useLimitTabToVisible(
  containerRef: React.RefObject<HTMLElement>,
  itemRefs: React.RefObject<HTMLElement>[],
  threshold = 0.75
) {
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    if (!container.hasAttribute('tabindex')) container.setAttribute('tabindex', '-1')

    const isElementVisible = (element: HTMLElement): boolean => {
      if (!container) return false
      const containerRect = container.getBoundingClientRect()
      const elementRect = element.getBoundingClientRect()

      const intersectionWidth = Math.max(
        0,
        Math.min(containerRect.right, elementRect.right) - Math.max(containerRect.left, elementRect.left)
      )
      const elementWidth = elementRect.width

      return elementWidth > 0 && intersectionWidth / elementWidth >= threshold
    }

    itemRefs.forEach((r) => {
      if (r.current) {
        if (isElementVisible(r.current)) {
          enableTabbing(r.current)
        } else {
          disableTabbing(r.current)
        }
      }
    })

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const el = entry.target as HTMLElement
          if (entry.isIntersecting) {
            enableTabbing(el)
          } else {
            if (el.contains(document.activeElement)) {
              container.focus?.()
            }
            disableTabbing(el)
          }
        })
      },
      { root: container, threshold }
    )

    itemRefs.forEach((r) => r.current && io.observe(r.current))
    return () => io.disconnect()
  }, [containerRef, itemRefs, threshold])
}
